import { useCallback, useEffect, useRef, useState } from "react";
import * as Tone from "tone";
import {
  buildVoicing,
  randomKeyMidi,
  ChordContext,
  ChordDegree,
  PROGRESSIONS,
} from "./harmonyEngine";
import { SampleKit } from "./useSamples";

export interface ChordState {
  name: string;
  notes: string[];
}

export interface HarmonyConfig {
  enabled: boolean;
  progressionLock: number | null;   // null = use pool randomly; number = always use that index
  keyRootOverride: number | null;   // null = random; 0-11 = pitch class, anchored to octave 3
  progressionPool: number[] | null; // null = all progressions; array = restrict to these indices
  registerOffset: number;           // semitone shift for chord voicing anchor
  rollChance: number;               // per-chord probability of rolling (0–1)
  passingChordChance: number;       // per-chord probability of passing chord (0–1)
}

export interface UseHarmonyResult {
  chord: ChordState | null;
  chordContextRef: { current: ChordContext | null };
  skip: () => void;
}

function randomRollPattern(length: number, chance = 0.5): boolean[] {
  return Array.from({ length }, () => Math.random() < chance);
}

function pickProgressionIndex(pool: number[] | null, excluding: number | null): number {
  const available = (pool ?? PROGRESSIONS.map((_, i) => i))
    .filter(i => i !== excluding && i < PROGRESSIONS.length);
  if (available.length === 0) return 0;
  return available[Math.floor(Math.random() * available.length)];
}

interface HarmonyState {
  progression: ChordDegree[];
  progressionIndex: number;
  index: number;
  keyRootMidi: number;
  rollPattern: boolean[];
  passingChordPattern: boolean[];
}

export function useHarmony(
  samples: SampleKit | null,
  configRef: { current: HarmonyConfig },
  chordSamplerRef: { current: Tone.Sampler | null },
): UseHarmonyResult {
  const [chord, setChord] = useState<ChordState | null>(null);
  const stateRef = useRef<HarmonyState | null>(null);
  const chordContextRef = useRef<ChordContext | null>(null);
  const samplesRef = useRef<SampleKit | null>(null);

  useEffect(() => {
    samplesRef.current = samples;
  }, [samples]);

  useEffect(() => {
    if (!samples) return;

    const cfg0 = configRef.current;
    const initialProgIdx = pickProgressionIndex(cfg0.progressionPool, null);
    const initialProg = PROGRESSIONS[initialProgIdx];
    stateRef.current = {
      progression: initialProg,
      progressionIndex: initialProgIdx,
      index: 0,
      keyRootMidi: randomKeyMidi(),
      rollPattern: randomRollPattern(initialProg.length, cfg0.rollChance),
      passingChordPattern: randomRollPattern(initialProg.length, cfg0.passingChordChance),
    };

    const id = Tone.Transport.scheduleRepeat((time) => {
      const state = stateRef.current;
      if (!state) return;

      const cfg = configRef.current;
      const progression = cfg.progressionLock !== null
        ? PROGRESSIONS[cfg.progressionLock]
        : state.progression;
      const keyRoot = cfg.keyRootOverride !== null
        ? 48 + cfg.keyRootOverride
        : state.keyRootMidi;
      const regOffset = cfg.registerOffset ?? 0;

      const degree = progression[state.index % progression.length];
      const voicing = buildVoicing(keyRoot, degree, regOffset);

      if (cfg.enabled) {
        const sampler = chordSamplerRef.current;
        const eighth = Tone.Time("8n").toSeconds();

        // Roll pattern decided once at song start — each chord position is either rolled or flat
        const chordPos = state.index % progression.length;
        const stagger = state.rollPattern[chordPos] ? 0.15 : 0;

        const noteCount = voicing.notes.length;
        voicing.notes.forEach((note, i) => {
          const vel = stagger > 0
            ? 0.4 + (i / Math.max(noteCount - 1, 1)) * 0.3  // crescendo 0.4 → 0.7 when rolled
            : 1.0;
          sampler?.triggerAttackRelease(note, "1m", time + i * stagger, vel);
        });

        // Diatonic approach to the NEXT chord — per-position pattern decided once at song start
        if (state.passingChordPattern[chordPos]) {
          const nextIndex = (state.index + 1) % progression.length;
          const nextDegree = progression[nextIndex];
          const direction = Math.random() < 0.7 ? -2 : 2; // whole step — diatonic
          const passingVoicing = buildVoicing(keyRoot, { semitones: nextDegree.semitones + direction, type: nextDegree.type }, regOffset);
          const barSeconds = Tone.Time("1m").toSeconds();
          const offset = Math.random() < 0.5 ? eighth : Tone.Time("16n").toSeconds();
          // Passing chord ~6dB softer (velocity ≈ 0.5) than the main chord
          passingVoicing.notes.forEach((note, i) => {
            sampler?.triggerAttackRelease(note, "4n", time + barSeconds - offset + i * stagger, 0.5);
          });
        }
      }

      chordContextRef.current = { keyRootMidi: keyRoot, degree };
      setChord(voicing);
      state.index = (state.index + 1) % progression.length;
    }, "1m", 0);

    return () => {
      Tone.Transport.clear(id);
      stateRef.current = null;
      chordContextRef.current = null;
      setChord(null);
    };
  }, [samples, configRef, chordSamplerRef]);

  const skip = useCallback(() => {
    const cfg = configRef.current;
    const newKey = cfg.keyRootOverride !== null ? 48 + cfg.keyRootOverride : randomKeyMidi();
    const newProgIdx = cfg.progressionLock !== null
      ? cfg.progressionLock
      : pickProgressionIndex(cfg.progressionPool, stateRef.current?.progressionIndex ?? null);
    const newProg = PROGRESSIONS[newProgIdx];
    const degree = newProg[0];
    const regOffset = cfg.registerOffset ?? 0;
    const voicing = buildVoicing(newKey, degree, regOffset);

    stateRef.current = {
      progression: newProg,
      progressionIndex: newProgIdx,
      index: 1,
      keyRootMidi: newKey,
      rollPattern: randomRollPattern(newProg.length, cfg.rollChance),
      passingChordPattern: randomRollPattern(newProg.length, cfg.passingChordChance),
    };
    chordContextRef.current = { keyRootMidi: newKey, degree };
    setChord(voicing);

    const sampler = chordSamplerRef.current;
    if (sampler && Tone.Transport.state === "started" && cfg.enabled) {
      voicing.notes.forEach((note) => {
        sampler.triggerAttackRelease(note, "1m", Tone.now() + 0.05);
      });
    }
  }, [configRef, chordSamplerRef]);

  return { chord, chordContextRef, skip };
}
